import { ChangeDetectorRef, Component, computed, DestroyRef, forwardRef, HostBinding, inject, input, model, OnInit, output, signal, viewChild } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { HumanPipe } from '../../../shared/pipes/human.pipe';
import { NgClass, TitleCasePipe } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { FormArray, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, lastValueFrom, tap, throwError } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CheckIn } from '../../../models/checkin.model';
import { Client } from '../../../models/client.model';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { formatIdentifier } from '../../../utils/client-utils';
import { AdminService } from '../../../admin/services/admin.service';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { InventoryActionFormComponent } from '../../../shared/common-forms/inventory-action-form/inventory-action-form.component';
import { FormGroupPipe } from '../../../shared/pipes/form-group.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { InvoiceService } from '../../invoice.service';
import { DocumentSelectComponent } from '../../../shared/document-select/document-select.component';
import { Budget } from '../../../models/budget.model';
import { toInventoryActionProductForm } from '../../../models/inventory-action-product';
import { INVOICE_TYPES } from '../../../models/invoice.model';
import { BillingInfoFormComponent } from '../../../shared/common-forms/billing-info-form/billing-info-form.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClientSelectComponent } from '../../../client/client-select/client-select.component';
import { ProviderSelectComponent } from '../../../providers/provider-select/provider-select.component';
import { AllocationsFormComponent } from '../../../shared/common-forms/allocations-form/allocations-form.component';
import { InvoiceDetailsFormComponent } from '../details/details.component';

@Component({
  selector: 'app-invoice-create-form',
  imports: [
    AllocationsFormComponent,
    forwardRef(() => BillingInfoFormComponent),
    forwardRef(() => ClientSelectComponent),
    CurrencyMaskModule,
    forwardRef(() => DocumentSelectComponent),
    forwardRef(() => FormGroupPipe),
    FormsModule,
    HumanPipe,
    InvoiceDetailsFormComponent,
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogClose,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatTooltipModule,
    NgClass,
    forwardRef(() => ProviderSelectComponent),
    ReactiveFormsModule,
    TitleCasePipe,
],
  templateUrl: './create.component.html',
  styleUrl: './create.component.scss',
})
export class InvoiceCreateFormComponent implements OnInit {

  enableDocumentSelection = input<boolean>();
  
  isPluginMode = input<boolean>(false);
  
  sourceDocument = input<unknown>(undefined);
  
  sourceDocumentType = input<string | undefined>();

  workInstanceId = input<number | undefined>();


  updated = output<void>();


  alreadyPayed = signal<boolean>(true);

  private readonly adminService = inject(AdminService);

  clients = signal<Client[]>([]);

  createFromDocumentMode = model(false);

  currentDate = new Date();

  private readonly destroyRef = inject(DestroyRef);

  private readonly dialogRef = inject(MatDialogRef<InvoiceCreateFormComponent>);

  displayClientFn = (client_id) => this.clients()?.find((client) => client.id === client_id)?.name;

  displayDocumentFn = (order) => order?.correlative_id;
  
  documents = signal<CheckIn[]>([]);

  private readonly invoiceService = inject(InvoiceService);

  loading = signal<boolean>(false);

  originalDetails = signal<any[]>([]);

  selectedClient = signal<Client | undefined>(undefined);

  selectedDocument = signal<any | undefined>(undefined);

  SOURCE_DOCUMENT_TYPES = ['Order', 'Budget'];

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly fb = inject(UntypedFormBuilder);

  private readonly detailsFormComponent = viewChild(InvoiceDetailsFormComponent);
  

  form = this.fb.group({
    stakeholder_id: this.fb.control(null, Validators.required),
    stakeholder_type: this.fb.control('Client', Validators.required),
    document_id: [null],
    document_type: [null],
    invoice_type: [null, Validators.required],
    is_buy: this.fb.control(false),
    data: this.fb.group({
      allocations_attributes: this.fb.array([]),
      client: this.fb.group({
        code: this.fb.control({ value: '', disabled: true },),
        name: this.fb.control({ value: '', disabled: true },),
        company: this.fb.control(null),
        activity: this.fb.control(null),
      }),
      details: this.fb.array([]),
      billing_info: this.fb.control(null, Validators.required),
      declare_to_sii: this.fb.control(true, { nonNullable: true }),
      note: this.fb.control(null),
    }),
  });
  
  invoiceTypes = INVOICE_TYPES;

  readonly data = inject(MAT_DIALOG_DATA);

  _enableDocumentSelection = computed(() => this.enableDocumentSelection() || this.data?.enableDocumentSelection);
  _sourceDocument = computed(() => this.sourceDocument() || this.data?.sourceDocument);
  _sourceDocumentType = computed(() => this.sourceDocumentType() || this.data?.sourceDocumentType);


  @HostBinding('class') background;

  
  get details() {
    return this.form.get('data.details') as FormArray;
  }
  
  set details(value) {
    (this.form.controls.data as FormGroup).controls.details = value;
  }
  
  get documentType(): string {
    return this.form.get('document_type').value;
  }
  
  get formValid() {
    return this.form.valid && this.isPaid && this.details.getRawValue().length > 0;
  }

  get paymentsFormArray() {
    return this.form.get('data.allocations_attributes') as FormArray;
  }

  get totalAmountAllocated() {
    return this.paymentsFormArray.getRawValue().reduce((acc, item) => {
      return acc + item.allocated_amount;
    }, 0);
  }

  get isPaid() {
    return this.totalAmountAllocated === this.total;
  }

  get isElectronicInvoice() {
    return this.form.get('invoice_type').value === 'electronic_invoice';
  }

  get shouldEnterReceiptNumber(): boolean {
    return ['credit_card', 'debit_card'].includes(this.form.get('data.payment.payment_method').value);
  }

  get total() {
    return Math.round((this.detailsFormComponent()?.getTotal() || 0) * this.adminService.getTaxValue()) ;
  }

  ngOnInit() {
    if (this.isPluginMode()) {
      this.background = 'plugin-mode';
      this.cdr.detectChanges();
    }

    this.setFormHandlers();
    this.setFormControlValidators();

    if (!this._enableDocumentSelection()) {
      this.form.get('document_type').setValue(this._sourceDocumentType());
      this.form.get('document_id').setValue(this._sourceDocument()?.id);
      this.onDocumentLoad(this._sourceDocument());
    }

    this.cdr.detectChanges();
  }

  private setFormHandlers() {
    this.form.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.updated.emit();
    });

    this.setClientFormHandlers();
  }

  onClientLoad(client: any) {
    if (!client) {
      this.form.get('data.client').reset();
      this.form.get('data.billing_info').reset();
      (this.form.get('data.allocations_attributes') as FormArray).clear();
      this.selectedClient.set(undefined);
      return;
    }

    this.form.get('data.client').patchValue({
      code: formatIdentifier(client?.rut, this.adminService.local$.getValue().country.code),
      name: client?.name,
      company: client?.name,
    });

    this.selectedClient.set(client);
  }

  private setClientFormHandlers() {
    this.form.get('stakeholder_type').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.form.get('data').reset();
      this.form.get('stakeholder_id').reset();
      (this.form.get('data.allocations_attributes') as FormArray).clear();
    });
  }

  private setFormControlValidators() {
    this.form.get('invoice_type').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (this.isElectronicInvoice) {
        this.form.get('data.client.company').setValidators([Validators.required]);
        this.form.get('data.client.activity').setValidators([Validators.required]);
        this.form.get('data.client.company').updateValueAndValidity();
        this.form.get('data.client.activity').updateValueAndValidity();
      } else {
        this.form.get('data.client.company').clearValidators();
        this.form.get('data.client.activity').clearValidators();
        this.form.get('data.client.company').updateValueAndValidity();
        this.form.get('data.client.activity').updateValueAndValidity();
      }
    });
  }

  private resetForm() {
    this.form.get('data').reset();
    this.form.get('invoice_type').reset();
    this.form.get('stakeholder_id').reset();
    this.form.get('stakeholder_type').reset();
    this.form.get('data.details').reset();
  }

  toggleCreateFromDocumentMode(enabled: boolean) {
    if (!enabled) {
      this.form.get('document_id').setValue(null, { emitEvent: false });
      this.form.get('document_type').setValue(null, { emitEvent: false });
    }
  }

  public onDocumentLoad(document: any): void {
    if (!document) {
      this.resetForm();
      return;
    }

    let products = [];
    let client: Client;
    this.selectedDocument.set(document);

    if (this.documentType === 'Order') {
      document = document as CheckIn;
      client = document.client;
      const requirement = document.requirement;
      products = [
        ...(document.inventory_action_for_consume
                   ?.inventory_action_products
                    .map(iap => ({
                      ...iap,
                      product: {...iap.product, name: iap.product.name + ` (${requirement.name})` }
                    })
                    ) || []),
        ...document.budgets.flatMap(
          (budget) => budget.inventory_actions.flatMap(
            (inventoryAction) => inventoryAction.inventory_action_products.map(iap => ({
              ...iap,
              product: {...iap.product, name: iap.product.name + ` (${inventoryAction.tag?.name})` }
            }))
          )
        ),
      ];
    }
    else if (this.documentType === 'Budget') {
      document = document as Budget;
      client = document.order.client;
      products = document.inventory_actions.flatMap(
        (inventoryAction) => inventoryAction.inventory_action_products
      );
    }
    
    this.details = this.fb.array(
      products.map(
        (product) => toInventoryActionProductForm({ ...product, id: undefined }),
      )
    );

    this.originalDetails.set(products);

    this.form.get('stakeholder_id').setValue(client);
    this.form.get('stakeholder_type').setValue('Client', { emitEvent: false });
  };

  saveForm(): Promise<any> {

    this.loading.set(true);
    const formData = this.form.getRawValue();
    const body: any = {
      data: {
        ...formData.data,
        allocations_attributes: this.form.get('data.allocations_attributes').value,
        total: this.detailsFormComponent()?.getTotal(),
      },
      document_id: formData.document_id,
      document_type: formData.document_type,
      invoice_type: formData.invoice_type,
      is_buy: formData.is_buy,
      stakeholder_id: formData.stakeholder_id,
      stakeholder_type: formData.stakeholder_type,
      type: 'Invoice::Sale',
    }


    if (this.isPluginMode()) {
      body.work_instance_id = this.workInstanceId();
    }

    return lastValueFrom(this.invoiceService.create(body).pipe(
        catchError((error) => {
          console.error('Error creating invoice:', error);
          this.loading.set(false);
          return throwError(() => error);
        }),
        tap(() => {
          this.loading.set(false);
          if (!this.isPluginMode()) {
            this.dialogRef?.close(true);
          }
        }),
    ));
  }

  autocompleteAmount() {
    this.form.get('data.payment.amount').setValue(this.total);
  }
}
